import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wine as BottleIcon, ShieldCheck, AlertTriangle, Coins, Sparkles, Loader2, Trophy, Info } from "lucide-react";

import { _t } from "@/lib/i18n";
const CONTEST_OPTIONS = [
  { id: "none",           label: _t("Nessun contest"),             desc: _t("Solo condivisione pubblica") },
  { id: "first_reaction", label: _t("Prima reazione vince"),       desc: _t("Il primo Dreamer che reagisce dopo l'apertura vince") },
  { id: "first_comment",  label: _t("Primo commento vince"),       desc: _t("Il primo che commenta dopo l'apertura vince") },
  { id: "riddle",         label: _t("Enigma da risolvere"),        desc: _t("Chi risponde correttamente per primo vince") },
];

export default function MiabCreate() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [openAt, setOpenAt] = useState(""); // YYYY-MM-DD
  const [purpose, setPurpose] = useState("personal");
  const [contest, setContest] = useState("none");
  const [prizeNote, setPrizeNote] = useState("");
  const [riddleQ, setRiddleQ] = useState("");
  const [riddleA, setRiddleA] = useState("");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{data: u}, {data: p}] = await Promise.all([
          api.get("/auth/me"),
          api.get("/miab/pricing"),
        ]);
        setMe(u);
        setPricing(p);
        // Default open_at = oggi + 1 anno + 1 giorno
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        d.setDate(d.getDate() + 1);
        setOpenAt(d.toISOString().slice(0, 10));
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  // Ricalcola preventivo ogni volta che cambia qualcosa di rilevante
  useEffect(() => {
    if (!openAt || !message) { setQuote(null); return; }
    const size_bytes = new Blob([message]).size;
    const iso = new Date(openAt + "T12:00:00Z").toISOString();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/miab/quote", {
          size_bytes, open_at: iso, content_purpose: purpose,
        });
        setQuote(data.quote);
      } catch (e) {
        setQuote({ error: formatApiError(e) });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [message, openAt, purpose]);

  const canSubmit = title.trim().length >= 3 && message.trim().length >= 10 && openAt &&
    (contest !== "riddle" || (riddleQ.trim() && riddleA.trim())) &&
    quote && !quote.error;

  const identityReady = me && me.two_factor_enabled && (me.email_verified !== false);

  const submit = async () => {
    if (!identityReady) {
      toast.error(_t("Attiva prima 2FA e email verificata dal profilo."));
      return;
    }
    setSubmitting(true);
    try {
      const iso = new Date(openAt + "T12:00:00Z").toISOString();
      const body = {
        title: title.trim(), message: message.trim(), open_at: iso,
        content_purpose: purpose, contest_type: contest,
        contest_prize_note: prizeNote.trim() || null,
      };
      if (contest === "riddle") {
        body.riddle_question = riddleQ.trim();
        body.riddle_answer = riddleA.trim();
      }
      const { data } = await api.post("/miab/drafts", body);
      toast.success(_t("Bozza creata. Procedo con il lancio…"));
      const { data: r } = await api.post(`/miab/${data.id}/launch`);
      if (r.state === "IN_REVISIONE_MIAB") {
        toast.warning(_t("Un moderatore verificherà la natura del messaggio prima della sigillatura."));
        nav("/miab");
      } else if (r.state === "IN_ATTESA_PAGAMENTO") {
        // Real Stripe Checkout — the amount is computed and validated server-side.
        // The frontend only sends the miab_id + origin_url.
        toast.info(_t("Prezzo: € {0}. Ti reindirizzo a Stripe…", r.price_snapshot.total_eur.toFixed(2)));
        try {
          const { data: chk } = await api.post("/payments/checkout", {
            service: "miab_launch",
            origin_url: window.location.origin,
            params: { miab_id: data.id },
          });
          window.location.href = chk.checkout_url;
        } catch (err) {
          toast.error(formatApiError(err));
          setSubmitting(false);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="space-y-2">
          <h1 className="font-display text-4xl text-white flex items-center gap-3">
            <BottleIcon className="w-9 h-9 text-cyan-400" />{" "}{_t("Lancia un Message in a Bottle")}</h1>
          <p className="text-white/60 text-sm">{_t("Il tuo messaggio si aprirà per tutta la community a una data futura (min. 1 anno). Il tuo nome sarà visibile.")}</p>
        </header>

        {/* Identity gate */}
        {me && !identityReady && (
          <div data-testid="miab-identity-warning" className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-4 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100/90">
              <div className="font-semibold text-amber-200 mb-1">{_t("Verifica identità richiesta")}</div>{_t("Per pubblicare un MIAB serve")}{" "}<strong>{_t("2FA attivo")}</strong>{" "}{_t("ed")}{" "}<strong>{_t("email verificata")}</strong>{_t(". Vai al")}{" "}<a href="/profilo" className="underline text-white">{_t("tuo profilo")}</a>{" "}{_t("per configurarli.")}</div>
          </div>
        )}

        {/* Content */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <label className="text-xs text-white/50 uppercase tracking-widest">{_t("Titolo")}</label>
          <input data-testid="miab-title" value={title} onChange={(e)=>setTitle(e.target.value)} maxLength={140}
                 placeholder={_t("Un titolo evocativo (max 140 caratteri)")}
                 className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white" />
          <label className="text-xs text-white/50 uppercase tracking-widest">{_t("Messaggio")}</label>
          <textarea data-testid="miab-message" value={message} onChange={(e)=>setMessage(e.target.value)} rows={7} maxLength={8000}
                    placeholder={_t("Cosa vuoi dire al mondo tra qualche anno? (10 – 8000 caratteri)")}
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white" />
          <div className="text-xs text-white/40 text-right">{message.length} / 8000</div>
        </section>

        {/* Purpose */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <div className="text-xs text-white/50 uppercase tracking-widest">{_t("Finalità del contenuto")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { id: "personal",   title: _t("Personale"),    desc: _t("Ricordo, riflessione, messaggio emotivo"),   Icon: Sparkles },
              { id: "commercial", title: _t("Commerciale"),  desc: _t("Contenuto promozionale o pubblicitario"),     Icon: Coins },
            ].map(({id, title, desc, Icon}) => (
              <button key={id} data-testid={`miab-purpose-${id}`} onClick={()=>setPurpose(id)}
                      className={`rounded-xl p-4 text-left border transition-colors ${
                        purpose === id ? "border-[#FF5722] bg-[#FF5722]/10" : "border-white/15 hover:border-white/30"
                      }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${purpose===id?"text-[#FF5722]":"text-white/50"}`} />
                  <div className="text-white font-semibold">{title}</div>
                  {id === "commercial" && (
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-200 bg-amber-500/5">{_t("Premium ×{0}", pricing?.commercial_multiplier || 5)}</span>
                  )}
                </div>
                <div className="text-xs text-white/60">{desc}</div>
              </button>
            ))}
          </div>
          {purpose === "personal" && (
            <div className="text-xs text-amber-200/80 flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{_t("Se il nostro sistema rileva un contenuto commerciale nascosto, la capsula sarà messa in revisione e potrebbe essere riclassificata.")}</div>
          )}
        </section>

        {/* Open at */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <label className="text-xs text-white/50 uppercase tracking-widest">{_t("Data di apertura pubblica")}</label>
          <input data-testid="miab-open-at" type="date" value={openAt} onChange={(e)=>setOpenAt(e.target.value)}
                 className="bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white" />
          <div className="text-xs text-white/50">{_t("Minimo: {0} anno · Massimo: {1} anni", pricing?.min_years_ahead || 1, pricing?.max_years_ahead || 50)}</div>
        </section>

        {/* Contest */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
          <div className="text-xs text-white/50 uppercase tracking-widest flex items-center gap-2">
            <Trophy className="w-3 h-3" />{" "}{_t("Contest temporale (opzionale)")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {CONTEST_OPTIONS.map((c) => (
              <button key={c.id} data-testid={`miab-contest-${c.id}`} onClick={()=>setContest(c.id)}
                      className={`rounded-xl p-3 text-left border ${contest===c.id ? "border-[#FF5722] bg-[#FF5722]/10" : "border-white/15 hover:border-white/30"}`}>
                <div className="text-white text-sm font-semibold">{c.label}</div>
                <div className="text-xs text-white/60">{c.desc}</div>
              </button>
            ))}
          </div>
          {contest !== "none" && (
            <input data-testid="miab-prize-note" value={prizeNote} onChange={(e)=>setPrizeNote(e.target.value)} maxLength={300}
                   placeholder={_t("Descrizione del premio simbolico (opzionale)")}
                   className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-white text-sm" />
          )}
          {contest === "riddle" && (
            <div className="space-y-2 pt-2">
              <input data-testid="miab-riddle-q" value={riddleQ} onChange={(e)=>setRiddleQ(e.target.value)} maxLength={500}
                     placeholder={_t("Domanda dell'enigma")}
                     className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-white text-sm" />
              <input data-testid="miab-riddle-a" value={riddleA} onChange={(e)=>setRiddleA(e.target.value)} maxLength={200}
                     placeholder={_t("Risposta esatta (case-insensitive)")}
                     className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-white text-sm" />
            </div>
          )}
        </section>

        {/* Price preview */}
        <section data-testid="miab-quote" className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-5 space-y-2">
          <div className="text-xs text-cyan-200 uppercase tracking-widest flex items-center gap-2">
            <Info className="w-3 h-3" />{" "}{_t("Preventivo dinamico")}</div>
          {!quote ? (
            <div className="text-white/50 text-sm">{_t("Compila messaggio e data per vedere il prezzo…")}</div>
          ) : quote.error ? (
            <div className="text-rose-300 text-sm">{quote.error}</div>
          ) : (
            <>
              <div className="text-3xl text-white font-display">€ {quote.total_eur.toFixed(2)}</div>
              <div className="text-xs text-white/60 space-y-0.5">
                <div>{_t("Peso testo: {0} MB · Anni: {1}", quote.size_mb.toFixed(3), quote.years_ahead.toFixed(2))}</div>
                <div>{_t("Base € {0} + peso × € {1}/MB + anni × € {2}/anno + incrocio", quote.base_eur, quote.per_mb_eur, quote.per_year_eur)}</div>
                {quote.commercial && (
                  <div className="text-amber-200">{_t("Tariffa Premium commerciale ×{0}", quote.commercial_multiplier)}</div>
                )}
              </div>
            </>
          )}
        </section>

        <div className="flex justify-end">
          <button data-testid="miab-submit-btn" onClick={submit} disabled={!canSubmit || submitting || !identityReady}
                  className="btn-primary !py-3 !px-6 disabled:opacity-40 inline-flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? _t("Sigillo…") : _t("Lancia il MIAB{0}", quote?.total_eur ? ` · € ${quote.total_eur.toFixed(2)}` : "")}
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
